/**
 * Stroke geometry for the `ScribbleBar`: pure functions from the bar's size to
 * SVG path strings, kept out of the component so containment is unit testable.
 *
 * A cubic bezier stays within the convex hull of its control points, so keeping
 * every point half a stroke (plus wiggle) inside the per-edge insets keeps the
 * drawn ink inside them too.
 */
import { PARAMS } from './params';

/** Rendered stroke width in px. */
export const EFFECTIVE_STROKE_WIDTH: number = Math.max(20 * PARAMS.strokeWidth, 6);

const HALF_STROKE = EFFECTIVE_STROKE_WIDTH / 2;

/** Box top to the highest allowed stroke center line. */
export const VERTICAL_INK_MARGIN_TOP: number = PARAMS.insetTop + HALF_STROKE + PARAMS.wiggle;

/** Box bottom to the lowest allowed stroke center line. */
export const VERTICAL_INK_MARGIN_BOTTOM: number = PARAMS.insetBottom + HALF_STROKE + PARAMS.wiggle;

/** Box side to the nearest allowed stroke end point (round cap included). */
export const HORIZONTAL_INK_MARGIN: number = PARAMS.insetX + HALF_STROKE;

const REFERENCE_LINE_HEIGHT = 20;

const clamp = (value: number, min: number, max: number): number =>
	Math.min(Math.max(value, min), max);

const random = (amplitude: number): number => (Math.random() * 2 - 1) * amplitude;

export type Stroke = {
	cx1x: number;
	cx1y: number;
	cx2x: number;
	cx2y: number;
	x0: number;
	x3: number;
	y: number;
};

const cubic = (p0: number, p1: number, p2: number, p3: number, t: number): number => {
	const u = 1 - t;
	return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
};

/** Y of a stroke's center line at `x`; x(t) is monotonic so bisection finds t. */
export const strokeYAtX = (s: Stroke, x: number): number => {
	const leftToRight = s.x0 <= s.x3;
	const target = clamp(x, Math.min(s.x0, s.x3), Math.max(s.x0, s.x3));
	let lo = 0;
	let hi = 1;
	for (let i = 0; i < 24; i++) {
		const mid = (lo + hi) / 2;
		const xm = cubic(s.x0, s.cx1x, s.cx2x, s.x3, mid);
		if (xm < target === leftToRight) {
			lo = mid;
		} else {
			hi = mid;
		}
	}
	const t = (lo + hi) / 2;
	return cubic(s.y, s.cx1y, s.cx2y, s.y, t);
};

const serialize = ({ x0, y, cx1x, cx1y, cx2x, cx2y, x3 }: Stroke, dy: number): string =>
	`M ${x0.toFixed(2)} ${(y + dy).toFixed(2)} C ${cx1x.toFixed(2)} ${(cx1y + dy).toFixed(
		2,
	)} ${cx2x.toFixed(2)} ${(cx2y + dy).toFixed(2)} ${x3.toFixed(2)} ${(y + dy).toFixed(2)}`;

/**
 * Cubic-bezier path strings for a `width` x `height` box. Odd strokes run right
 * to left; each end stops a random distance short of the side margin.
 */
export function generateStrokes(width: number, height: number): string[] {
	const count = Math.max(Math.round(PARAMS.strokeCount * (height / REFERENCE_LINE_HEIGHT)), 1);

	// Allowed range for stroke center lines; collapses to the middle for tiny boxes.
	const bandTop = Math.min(VERTICAL_INK_MARGIN_TOP, height / 2);
	const bandBottom = Math.max(height - VERTICAL_INK_MARGIN_BOTTOM, bandTop);

	const xStart = Math.min(HORIZONTAL_INK_MARGIN, width / 2);
	const xEnd = Math.max(width - HORIZONTAL_INK_MARGIN, width / 2);
	const capJitter = Math.min(PARAMS.capJitter, (xEnd - xStart) / 4);

	const strokes = Array.from({ length: count }, (_, i): Stroke => {
		const t = count === 1 ? 0.5 : i / (count - 1);
		const yBase = bandTop + t * (bandBottom - bandTop);
		const isEdge = i === 0 || i === count - 1;
		const y = clamp(
			yBase + random(isEdge ? PARAMS.edgeJitter : PARAMS.jitter),
			bandTop,
			bandBottom,
		);

		const cx1y = y + random(PARAMS.wiggle);
		const cx2y = y + random(PARAMS.wiggle);

		const left = xStart + Math.random() * capJitter;
		const right = xEnd - Math.random() * capJitter;
		const span = right - left;

		const leftToRight = i % 2 === 0;
		const x0 = leftToRight ? left : right;
		const x3 = leftToRight ? right : left;
		const nearX = left + span * (0.3 + random(PARAMS.drift));
		const farX = left + span * (0.7 + random(PARAMS.drift));
		const cx1x = leftToRight ? nearX : farX;
		const cx2x = leftToRight ? farX : nearX;

		return { x0, y, cx1x, cx1y, cx2x, cx2y, x3 };
	});

	// Shift the stack so the ink above and below the Guard icon is centered on it.
	const iconHalf = Math.min(PARAMS.iconSize / 2, (xEnd - xStart) / 2);
	const samples = Array.from({ length: 5 }, (_, i) => width / 2 - iconHalf + (i * iconHalf) / 2);
	const localYs = strokes.flatMap((s) => samples.map((x) => strokeYAtX(s, x)));
	const localCenter = (Math.min(...localYs) + Math.max(...localYs)) / 2;
	const wanted = height / 2 + PARAMS.iconOffset - localCenter;

	// Clamp so the convex hull stays within the band.
	const hullYs = strokes.flatMap((s) => [s.y, s.cx1y, s.cx2y]);
	const minShift = bandTop - PARAMS.wiggle - Math.min(...hullYs);
	const maxShift = bandBottom + PARAMS.wiggle - Math.max(...hullYs);
	const dy = minShift <= maxShift ? clamp(wanted, minShift, maxShift) : (minShift + maxShift) / 2;

	return strokes.map((s) => serialize(s, dy));
}
